import {
    fileBody,
    restDelete,
    restGet,
    restGetBlob,
    restPost,
    restPut,
} from "apiCommandsExecutor/rest";

import {
    DeliveryConditionsZonePriceDto,
    ExternalProductOptionsDto,
    ExternalProductsPageDto,
    IikoExternalMenusDto,
    IikoIntegrationDto,
    IikoReferenceItemDto,
    IikoWebhookDto,
    MenuImportResultDto,
    MenuListDto,
    MenuCategoriesPageDto,
    MenuCategoryDto,
    MenuItemDto,
    MenuItemListDto,
    MenuItemsListFilterDto,
    MenuItemsPageDto,
    MenuOptionDto,
    MenuSettingsDto,
} from "./restModels";
import {
    AddIikoTerminalRequestDto,
    CopyMenuItemRequestDto,
    CreateMenuCategoryRequestDto,
    CreateMenuItemRequestDto,
    MoveMenuItemRequestDto,
    ReorderMenuCategoriesRequestDto,
    ReorderMenuItemsRequestDto,
    SetMenuItemStopListRequestDto,
    UpdateIikoApiLoginRequestDto,
    UpdateIikoIntegrationEnabledRequestDto,
    UpdateIikoCatalogSourceRequestDto,
    UpdateIikoPriceSyncEnabledRequestDto,
    ConnectIikoWebhookRequestDto,
    UpdateIikoOrganizationRequestDto,
    UpdateMenuCategoryRequestDto,
    UpdateMenuItemRequestDto,
    UpdateMenuSettingsRequestDto,
} from "./restRequests";

// ===== Позиция меню (REST /api/rest/warehouse/v1) =====

export const getMenuItem = (menuItemId: number) =>
    restGet<MenuItemDto>(`/menu-items/${menuItemId}`);

export const updateMenuItem = (menuItemId: number, body: UpdateMenuItemRequestDto) =>
    restPut<MenuItemDto>(`/menu-items/${menuItemId}`, body);

// ===== Категории меню =====

export const getMenuCategories = (menuId: number) =>
    restGet<MenuCategoriesPageDto>("/menu-categories", { menuId });

export const createMenuCategory = (body: CreateMenuCategoryRequestDto) =>
    restPost<MenuCategoryDto>("/menu-categories", body);

export const updateMenuCategory = (
    menuCategoryId: number,
    body: UpdateMenuCategoryRequestDto
) => restPut<MenuCategoryDto>(`/menu-categories/${menuCategoryId}`, body);

export const deleteMenuCategory = (menuCategoryId: number) =>
    restDelete<MenuCategoryDto>(`/menu-categories/${menuCategoryId}`);

export const reorderMenuCategories = (body: ReorderMenuCategoriesRequestDto) =>
    restPut<MenuCategoryDto[]>("/menu-categories/order", body);

// ===== Позиции: список и операции над одной позицией =====

export interface GetMenuItemsParams {
    menuId: number;
    categoryId?: number;
    query?: string;
    filter?: MenuItemsListFilterDto;
    skip: number;
    limit: number;
}

export const getMenuItems = (params: GetMenuItemsParams) =>
    restGet<MenuItemsPageDto>("/menu-items", { ...params });

export const createMenuItem = (body: CreateMenuItemRequestDto) =>
    restPost<MenuItemListDto>("/menu-items", body);

export const deleteMenuItem = (menuItemId: number) =>
    restDelete<MenuItemListDto>(`/menu-items/${menuItemId}`);

export const setMenuItemStopList = (
    menuItemId: number,
    body: SetMenuItemStopListRequestDto
) => restPut<MenuItemListDto>(`/menu-items/${menuItemId}/stop-list`, body);

export const moveMenuItem = (menuItemId: number, body: MoveMenuItemRequestDto) =>
    restPut<MenuItemListDto>(`/menu-items/${menuItemId}/category`, body);

export const copyMenuItem = (menuItemId: number, body: CopyMenuItemRequestDto) =>
    restPost<MenuItemListDto>(`/menu-items/${menuItemId}/copy`, body);

export const reorderMenuItems = (body: ReorderMenuItemsRequestDto) =>
    restPut<MenuItemListDto[]>("/menu-items/order", body);

// ===== Настройки меню =====

export const getMenus = () => restGet<MenuListDto[]>("/menus");

// Список для селектов: без счётчиков категорий и позиций, которые стоят бэку двух
// агрегаций по всем позициям филиала.
export const getMenuOptions = () => restGet<MenuOptionDto[]>("/menus/options");

export const getMenuSettings = (menuId: number) =>
    restGet<MenuSettingsDto>(`/menus/${menuId}`);

export const updateMenuSettings = (menuId: number, body: UpdateMenuSettingsRequestDto) =>
    restPut<MenuSettingsDto>(`/menus/${menuId}`, body);

// ===== Файлы меню: импорт позиций, шаблон, зоны доставки =====

/*
* Разбор строк файла — результат, а не сбой запроса: часть позиций могла завестись,
* поэтому проблемные строки приезжают списком с успешным ответом.
* */
export const importMenuItems = (menuId: number, file: File) =>
    restPost<MenuImportResultDto>(`/menus/${menuId}/import`, fileBody(file));

export const getMenuImportTemplate = () => restGetBlob("/menus/import-template");

// Границы зон рисуются в конструкторе Яндекс.Карт: бэк разбирает GeoJSON и отдаёт полигоны.
export const importDeliveryZones = (file: File) =>
    restPost<DeliveryConditionsZonePriceDto>("/menus/delivery-zones/import", fileBody(file));

// ===== Каталог внешнего меню (для привязки позиции к товару iiko) =====

export interface GetExternalProductsParams {
    menuId: number;
    query?: string;
    // Задан → ответ содержит ровно этот товар: так селект показывает уже привязанный,
    // не листая ради него весь каталог.
    externalId?: string;
    skip?: number;
    limit?: number;
}

export const getExternalProducts = (params: GetExternalProductsParams) =>
    restGet<ExternalProductsPageDto>("/menu-items/external-products", { ...params });

// ===== Опции внешнего товара (для привязки к меню iiko) =====

export const getExternalProductOptions = (menuId: number, externalProductId: string) =>
    restGet<ExternalProductOptionsDto>("/menu-items/external-product-options", {
        menuId,
        externalProductId,
    });

// ===== Интеграция меню с iiko =====

const iikoIntegrationPath = (menuId: number) => `/menus/${menuId}/iiko-integration`;

export const getIikoIntegration = (menuId: number) =>
    restGet<IikoIntegrationDto>(iikoIntegrationPath(menuId));

export const updateIikoApiLogin = (menuId: number, body: UpdateIikoApiLoginRequestDto) =>
    restPut<IikoIntegrationDto>(`${iikoIntegrationPath(menuId)}/api-login`, body);

export const updateIikoOrganization = (
    menuId: number,
    body: UpdateIikoOrganizationRequestDto
) => restPut<IikoIntegrationDto>(`${iikoIntegrationPath(menuId)}/organization`, body);

export const updateIikoIntegrationEnabled = (
    menuId: number,
    body: UpdateIikoIntegrationEnabledRequestDto
) => restPut<IikoIntegrationDto>(`${iikoIntegrationPath(menuId)}/enabled`, body);

export const getIikoWebhook = (menuId: number) =>
    restGet<IikoWebhookDto>(`${iikoIntegrationPath(menuId)}/webhook`);

export const connectIikoWebhook = (menuId: number, body: ConnectIikoWebhookRequestDto) =>
    restPost<IikoWebhookDto>(`${iikoIntegrationPath(menuId)}/webhook`, body);

export const getIikoExternalMenus = (menuId: number) =>
    restGet<IikoExternalMenusDto>(`${iikoIntegrationPath(menuId)}/external-menus`);

export const updateIikoCatalogSource = (
    menuId: number,
    body: UpdateIikoCatalogSourceRequestDto
) => restPut<IikoIntegrationDto>(`${iikoIntegrationPath(menuId)}/catalog-source`, body);

export const updateIikoPriceSyncEnabled = (
    menuId: number,
    body: UpdateIikoPriceSyncEnabledRequestDto
) => restPut<IikoIntegrationDto>(`${iikoIntegrationPath(menuId)}/price-sync`, body);

export const getIikoOrganizations = (menuId: number) =>
    restGet<IikoReferenceItemDto[]>(`${iikoIntegrationPath(menuId)}/organizations`);

export const getIikoTerminalGroups = (menuId: number) =>
    restGet<IikoReferenceItemDto[]>(`${iikoIntegrationPath(menuId)}/terminal-groups`);

export const addIikoTerminal = (menuId: number, body: AddIikoTerminalRequestDto) =>
    restPost<IikoIntegrationDto>(`${iikoIntegrationPath(menuId)}/terminals`, body);

export const removeIikoTerminal = (menuId: number, terminalGroupId: string) =>
    restDelete<IikoIntegrationDto>(
        `${iikoIntegrationPath(menuId)}/terminals/${encodeURIComponent(terminalGroupId)}`
    );

export const syncIikoNomenclature = (menuId: number) =>
    restPost<IikoIntegrationDto>(`${iikoIntegrationPath(menuId)}/sync`);
